import {
  formatMonthTitle,
  formatWeekTitle,
  getMonthBounds,
  getWeekBounds,
  parseDateValue,
  toIsoDate,
} from "../date/month";
import { buildCreateEventsFromCopiedEntry } from "./schedulerEvents";

const DAY_MS = 86_400_000;
export const PERIOD_COPY_MODES = {
  WEEK: "week",
  MONTH: "month",
  RANGE: "range",
};

export const CUSTOM_SHIFT_FILTER_ID = "__custom_shift__";
export const EMPTY_DEPARTMENT_FILTER_ID = "__without_department__";

/*
  Warum ein Eintrag liegen bleibt, als Code statt als Satz: die Oberfläche
  baut daraus einen eigenen Hinweis, und ein Satz taugt nicht als Schlüssel.
*/
export const PERIOD_COPY_SKIP_REASONS = {
  TARGET_OUT_OF_RANGE: "target_out_of_range",
  NOT_COPYABLE: "not_copyable",
};

function startOfDay(value) {
  const date = parseDateValue(value);
  return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
}

function addDays(value, amount) {
  const date = startOfDay(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

function dayDiff(left, right) {
  const leftDate = startOfDay(left);
  const rightDate = startOfDay(right);
  if (!leftDate || !rightDate) return 0;
  return Math.round((leftDate.getTime() - rightDate.getTime()) / DAY_MS);
}

function normalizeMode(value) {
  if (value === PERIOD_COPY_MODES.WEEK) return PERIOD_COPY_MODES.WEEK;
  if (value === PERIOD_COPY_MODES.RANGE) return PERIOD_COPY_MODES.RANGE;
  return PERIOD_COPY_MODES.MONTH;
}

function normalizeRange(startDate, endDate, fallbackDate) {
  const fallback = startOfDay(fallbackDate) || startOfDay(new Date()) || new Date();
  const start = startOfDay(startDate) || fallback;
  const end = startOfDay(endDate) || start;

  if (end < start) return { start: end, end: start };
  return { start, end };
}

export function getPeriodCopyBounds(viewMode, referenceDate, range = {}) {
  const mode = normalizeMode(viewMode);
  if (mode === PERIOD_COPY_MODES.WEEK) return getWeekBounds(referenceDate || range.startDate);
  if (mode === PERIOD_COPY_MODES.RANGE) {
    return normalizeRange(range.startDate || referenceDate, range.endDate, referenceDate);
  }
  return getMonthBounds(referenceDate || range.startDate);
}

function formatCompactDate(value, includeYear = true) {
  const date = parseDateValue(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("de-CH", {
    day: "2-digit",
    month: "short",
    year: includeYear ? "numeric" : undefined,
  }).format(date);
}

function formatRangeTitle(start, end) {
  const startDate = parseDateValue(start);
  const endDate = parseDateValue(end);
  if (!startDate || !endDate) return "Zeitraum";
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  return `${formatCompactDate(startDate, !sameYear)} - ${formatCompactDate(endDate, true)}`;
}

export function formatPeriodCopyLabel(viewMode, referenceDate, range = {}) {
  const mode = normalizeMode(viewMode);
  if (mode === PERIOD_COPY_MODES.WEEK) return formatWeekTitle(referenceDate || range.startDate);
  if (mode === PERIOD_COPY_MODES.RANGE) {
    const bounds = getPeriodCopyBounds(mode, referenceDate, range);
    return formatRangeTitle(bounds.start, bounds.end);
  }
  return formatMonthTitle(referenceDate || range.startDate);
}

function isDateInRange(dateKey, startKey, endKey) {
  const key = String(dateKey || "").slice(0, 10);
  return Boolean(key && key >= startKey && key <= endKey);
}

export function baseShiftFilterId(value) {
  const text = String(value ?? "").trim();
  return text ? text.split(":")[0] || text : "";
}

export function departmentFilterId(value) {
  const text = String(value ?? "").trim();
  return text ? text.toLocaleLowerCase("de") : EMPTY_DEPARTMENT_FILTER_ID;
}

function getShiftFilterId(shift) {
  const shiftId = baseShiftFilterId(shift?.shiftId);
  return shiftId || CUSTOM_SHIFT_FILTER_ID;
}

function normalizeShiftSelectionSet(selectedShiftIds) {
  if (selectedShiftIds == null) return null;
  return new Set(
    (Array.isArray(selectedShiftIds) ? selectedShiftIds : [])
      .map((value) => {
        const text = String(value ?? "").trim();
        return text === CUSTOM_SHIFT_FILTER_ID ? text : baseShiftFilterId(text);
      })
      .filter(Boolean)
  );
}

function normalizeDepartmentSelectionSet(selectedDepartmentIds) {
  if (selectedDepartmentIds == null) return null;
  return new Set(
    (Array.isArray(selectedDepartmentIds) ? selectedDepartmentIds : [])
      .map((value) => departmentFilterId(value))
      .filter(Boolean)
  );
}

function getShiftStartKey(shift) {
  return toIsoDate(shift?.start || shift?.dateKey);
}

function getHolidayStartKey(holiday) {
  return toIsoDate(holiday?.startDate);
}

function getKindLabel(kind) {
  if (kind === "absence") return "Absenz";
  if (kind === "holiday") return "Ferien";
  return "Schicht";
}

function getEntryTitle(entry, kind) {
  if (kind === "holiday") return entry?.holidayTypeName || "Ferien";
  return entry?.title || entry?.department || getKindLabel(kind);
}

function getEntryEmployeeName(entry, employeeNamesById) {
  const id = String(entry?.employeeId || entry?.userId || "");
  return entry?.employeeName || employeeNamesById.get(id) || "Mitarbeitende";
}

function makeTargetCell(entry, targetDateKey, employeeNamesById) {
  const id = String(entry?.employeeId || entry?.userId || "");
  if (!id || !targetDateKey) return null;

  return {
    rowMode: "employee",
    row: {
      id,
      name: getEntryEmployeeName(entry, employeeNamesById),
    },
    day: {
      key: targetDateKey,
      date: startOfDay(targetDateKey),
    },
  };
}

function buildEmployeeNameMap(shifts, userHolidays, employees) {
  const map = new Map();
  (Array.isArray(employees) ? employees : []).forEach((employee) => {
    const id = String(employee?.id || employee?.employeeId || "");
    if (id && employee?.name && !map.has(id)) map.set(id, employee.name);
  });
  (Array.isArray(shifts) ? shifts : []).forEach((shift) => {
    const id = String(shift?.employeeId || "");
    if (id && shift?.employeeName && !map.has(id)) map.set(id, shift.employeeName);
  });
  (Array.isArray(userHolidays) ? userHolidays : []).forEach((holiday) => {
    const id = String(holiday?.userId || "");
    if (id && holiday?.employeeName && !map.has(id)) map.set(id, holiday.employeeName);
  });
  return map;
}

function buildSourceItems({
  shifts,
  userHolidays,
  includeAbsences,
  includeHolidays,
  selectedShiftIds,
  selectedDepartmentIds,
  sourceStartKey,
  sourceEndKey,
}) {
  const selectedShiftSet = normalizeShiftSelectionSet(selectedShiftIds);
  const selectedDepartmentSet = normalizeDepartmentSelectionSet(selectedDepartmentIds);
  const shiftItems = (Array.isArray(shifts) ? shifts : [])
    .filter((shift) => {
      const kind = shift?.entryType === "absence" ? "absence" : "shift";
      if (kind === "absence" && !includeAbsences) return false;
      if (kind === "shift") {
        if (selectedShiftSet && !selectedShiftSet.has(getShiftFilterId(shift))) return false;
        if (selectedDepartmentSet && !selectedDepartmentSet.has(departmentFilterId(shift?.department))) return false;
      }
      return isDateInRange(getShiftStartKey(shift), sourceStartKey, sourceEndKey);
    })
    .map((shift) => ({
      kind: shift.entryType === "absence" ? "absence" : "shift",
      entry: shift,
      sourceDateKey: getShiftStartKey(shift),
    }));

  const holidayItems = includeHolidays
    ? (Array.isArray(userHolidays) ? userHolidays : [])
        .filter((holiday) => isDateInRange(getHolidayStartKey(holiday), sourceStartKey, sourceEndKey))
        .map((holiday) => ({
          kind: "holiday",
          entry: holiday,
          sourceDateKey: getHolidayStartKey(holiday),
        }))
    : [];

  return [...shiftItems, ...holidayItems].sort((left, right) => {
    const dateCompare = String(left.sourceDateKey).localeCompare(String(right.sourceDateKey));
    if (dateCompare !== 0) return dateCompare;
    return String(getEntryTitle(left.entry, left.kind)).localeCompare(String(getEntryTitle(right.entry, right.kind)), "de");
  });
}

/*
  Die liegengebliebenen Einträge nach Grund gebündelt. Die Tage kommen als
  Menge zurück, nicht als Liste: an einem Tag hängen meist mehrere Einträge,
  im Hinweis steht aber der Tag.
*/
function summarizeSkippedRows(skippedRows) {
  const outOfRangeDateKeys = new Set();
  let outOfRangeCount = 0;
  let notCopyableCount = 0;

  skippedRows.forEach((row) => {
    if (row?.reasonCode === PERIOD_COPY_SKIP_REASONS.NOT_COPYABLE) {
      notCopyableCount += 1;
      return;
    }
    outOfRangeCount += 1;
    if (row?.sourceDateKey) outOfRangeDateKeys.add(row.sourceDateKey);
  });

  return {
    outOfRange: {
      count: outOfRangeCount,
      dateKeys: Array.from(outOfRangeDateKeys).sort(),
    },
    notCopyable: { count: notCopyableCount },
  };
}

function formatEntryWord(count) {
  return count === 1 ? "1 Eintrag" : `${count} Einträge`;
}

function formatDayWord(count) {
  return count === 1 ? "1 Tag" : `${count} Tage`;
}

function formatSkippedDates(dateKeys) {
  const keys = (Array.isArray(dateKeys) ? dateKeys : []).filter(Boolean);
  if (!keys.length) return "";
  if (keys.length === 1) return formatCompactDate(keys[0]);
  return formatRangeTitle(keys[0], keys[keys.length - 1]);
}

/*
  "3 ausserhalb des Zielzeitraums" sagt nicht, warum. Der Grund ist fast immer
  die Länge: ein 31-Tage-Monat passt nicht in einen mit 30. Darum nennt der
  Hinweis beide Längen und die betroffenen Tage — sonst sucht man den Fehler
  bei sich.
*/
export function buildPeriodCopyNotices(preview) {
  const notices = [];
  const outOfRange = preview?.skipped?.outOfRange;
  const notCopyable = preview?.skipped?.notCopyable;
  const duplicates = preview?.counts?.duplicates || 0;

  if (outOfRange?.count) {
    const sourceDays = preview?.source?.dayCount || 0;
    const targetDays = preview?.target?.dayCount || 0;
    const missingDays = Math.max(0, sourceDays - targetDays);
    const dateLabel = formatSkippedDates(outOfRange.dateKeys);
    const lengthSentence = missingDays
      ? `Der Zielzeitraum ist ${formatDayWord(missingDays)} kürzer als die Quelle (${targetDays} statt ${sourceDays} Tage). `
      : "";
    const dateSentence =
      outOfRange.dateKeys.length === 1
        ? `Für den ${dateLabel} gibt es dort keinen passenden Tag`
        : `Für die Tage ${dateLabel} gibt es dort keine passenden Tage`;

    notices.push({
      id: "out-of-range",
      variant: "warning",
      title: `${formatEntryWord(outOfRange.count)} ${outOfRange.count === 1 ? "wird" : "werden"} nicht kopiert`,
      description: `${lengthSentence}${dateSentence} – trage diese Einträge bei Bedarf von Hand nach.`,
    });
  }

  if (notCopyable?.count) {
    notices.push({
      id: "not-copyable",
      variant: "warning",
      title: `${formatEntryWord(notCopyable.count)} ${notCopyable.count === 1 ? "lässt" : "lassen"} sich nicht kopieren`,
      description:
        notCopyable.count === 1
          ? "Dem Eintrag fehlt die Person, der er zugeordnet ist."
          : "Diesen Einträgen fehlt die Person, der sie zugeordnet sind.",
    });
  }

  if (duplicates) {
    notices.push({
      id: "duplicates",
      variant: "info",
      title: duplicates === 1 ? "1 mögliche Dublette" : `${duplicates} mögliche Dubletten`,
      description:
        duplicates === 1
          ? "Im Zielzeitraum steht bereits ein Eintrag mit denselben Angaben – nach dem Kopieren hast du ihn doppelt."
          : "Im Zielzeitraum stehen bereits Einträge mit denselben Angaben – nach dem Kopieren hast du sie doppelt.",
    });
  }

  return notices;
}

function getTargetDateForSource(sourceDateKey, sourceStartKey, targetStart, targetEndKey) {
  const offset = dayDiff(sourceDateKey, sourceStartKey);
  const targetDate = addDays(targetStart, offset);
  const targetDateKey = toIsoDate(targetDate);
  if (!targetDateKey || targetDateKey > targetEndKey) return "";
  return targetDateKey;
}

function eventDuplicateKey(event) {
  if (!event) return "";
  if (event.entry_type === "user_holiday") {
    return [
      "holiday",
      event.user_id,
      event.start_date,
      event.end_date,
      event.user_holiday_type_id || event.holiday_type_id,
    ].join("::");
  }

  return [
    event.entry_type || "shift",
    event.user_id,
    event.start_at,
    event.end_at,
    event.shift_id || "",
    event.absence_id || "",
    event.custom_shift_name || "",
  ].join("::");
}

function existingShiftDuplicateKey(shift) {
  if (!shift) return "";
  return [
    shift.entryType || "shift",
    shift.employeeId,
    shift.start,
    shift.end,
    shift.shiftId || "",
    shift.absenceId || "",
    shift.entryType === "custom_shift" ? shift.title || "" : "",
  ].join("::");
}

function existingHolidayDuplicateKey(holiday) {
  if (!holiday) return "";
  return ["holiday", holiday.userId, holiday.startDate, holiday.endDate, holiday.holidayTypeId].join("::");
}

function countTargetEntries({ shifts, userHolidays, targetStartKey, targetEndKey }) {
  const targetShifts = (Array.isArray(shifts) ? shifts : []).filter(
    (shift) => shift?.entryType !== "absence" && isDateInRange(getShiftStartKey(shift), targetStartKey, targetEndKey)
  );
  const targetAbsences = (Array.isArray(shifts) ? shifts : []).filter(
    (shift) => shift?.entryType === "absence" && isDateInRange(getShiftStartKey(shift), targetStartKey, targetEndKey)
  );
  const targetHolidays = (Array.isArray(userHolidays) ? userHolidays : []).filter((holiday) =>
    isDateInRange(getHolidayStartKey(holiday), targetStartKey, targetEndKey)
  );

  return {
    shifts: targetShifts.length,
    absences: targetAbsences.length,
    holidays: targetHolidays.length,
    total: targetShifts.length + targetAbsences.length + targetHolidays.length,
  };
}

function buildExistingDuplicateKeys(shifts, userHolidays) {
  const keys = new Set();
  (Array.isArray(shifts) ? shifts : []).forEach((shift) => {
    const key = existingShiftDuplicateKey(shift);
    if (key) keys.add(key);
  });
  (Array.isArray(userHolidays) ? userHolidays : []).forEach((holiday) => {
    const key = existingHolidayDuplicateKey(holiday);
    if (key) keys.add(key);
  });
  return keys;
}

export function buildPeriodCopyPreview({
  viewMode = PERIOD_COPY_MODES.MONTH,
  periodMode = viewMode,
  sourceReferenceDate,
  targetReferenceDate,
  sourceStartDate,
  sourceEndDate,
  targetStartDate,
  targetEndDate,
  shifts = [],
  userHolidays = [],
  employees = [],
  includeAbsences = false,
  includeHolidays,
  selectedShiftIds = null,
  selectedDepartmentIds = null,
} = {}) {
  const mode = normalizeMode(periodMode);
  const shouldIncludeAbsences = Boolean(includeAbsences);
  const shouldIncludeHolidays = includeHolidays == null ? shouldIncludeAbsences : Boolean(includeHolidays);
  const sourceBounds = getPeriodCopyBounds(mode, sourceReferenceDate, {
    startDate: sourceStartDate,
    endDate: sourceEndDate,
  });
  const targetBounds = getPeriodCopyBounds(mode, targetReferenceDate, {
    startDate: targetStartDate,
    endDate: targetEndDate,
  });
  const sourceStartKey = toIsoDate(sourceBounds.start);
  const sourceEndKey = toIsoDate(sourceBounds.end);
  const targetStartKey = toIsoDate(targetBounds.start);
  const targetEndKey = toIsoDate(targetBounds.end);
  const employeeNamesById = buildEmployeeNameMap(shifts, userHolidays, employees);
  const existingKeys = buildExistingDuplicateKeys(shifts, userHolidays);
  const items = buildSourceItems({
    shifts,
    userHolidays,
    includeAbsences: shouldIncludeAbsences,
    includeHolidays: shouldIncludeHolidays,
    selectedShiftIds,
    selectedDepartmentIds,
    sourceStartKey,
    sourceEndKey,
  });
  const rows = [];
  const skippedRows = [];
  const events = [];

  items.forEach((item) => {
    const targetDateKey = getTargetDateForSource(item.sourceDateKey, sourceStartKey, targetBounds.start, targetEndKey);
    const baseRow = {
      kind: item.kind,
      kindLabel: getKindLabel(item.kind),
      title: getEntryTitle(item.entry, item.kind),
      employeeName: getEntryEmployeeName(item.entry, employeeNamesById),
      sourceDateKey: item.sourceDateKey,
      targetDateKey,
      timeLabel: item.kind === "holiday" ? "Ganztag" : item.entry?.timeLabel || "",
    };

    if (!targetDateKey) {
      skippedRows.push({
        ...baseRow,
        reasonCode: PERIOD_COPY_SKIP_REASONS.TARGET_OUT_OF_RANGE,
        reason: "Zieldatum liegt ausserhalb des Zielzeitraums",
      });
      return;
    }

    const cell = makeTargetCell(item.entry, targetDateKey, employeeNamesById);
    const event = buildCreateEventsFromCopiedEntry(item.entry, cell ? [cell] : [])[0] || null;
    if (!event) {
      skippedRows.push({
        ...baseRow,
        reasonCode: PERIOD_COPY_SKIP_REASONS.NOT_COPYABLE,
        reason: "Eintrag kann nicht kopiert werden",
      });
      return;
    }

    const isDuplicate = existingKeys.has(eventDuplicateKey(event));
    events.push(event);
    rows.push({
      ...baseRow,
      event,
      isDuplicate,
    });
  });

  const counts = rows.reduce(
    (accumulator, row) => {
      if (row.kind === "shift") accumulator.shifts += 1;
      if (row.kind === "absence") accumulator.absences += 1;
      if (row.kind === "holiday") accumulator.holidays += 1;
      accumulator.total += 1;
      if (row.isDuplicate) accumulator.duplicates += 1;
      return accumulator;
    },
    { shifts: 0, absences: 0, holidays: 0, total: 0, duplicates: 0 }
  );

  return {
    mode,
    source: {
      startKey: sourceStartKey,
      endKey: sourceEndKey,
      dayCount: dayDiff(sourceBounds.end, sourceBounds.start) + 1,
      label: formatPeriodCopyLabel(mode, sourceBounds.start, {
        startDate: sourceBounds.start,
        endDate: sourceBounds.end,
      }),
    },
    target: {
      startKey: targetStartKey,
      endKey: targetEndKey,
      dayCount: dayDiff(targetBounds.end, targetBounds.start) + 1,
      label: formatPeriodCopyLabel(mode, targetBounds.start, {
        startDate: targetBounds.start,
        endDate: targetBounds.end,
      }),
    },
    includeAbsences: shouldIncludeAbsences,
    includeHolidays: shouldIncludeHolidays,
    selectedShiftIds,
    selectedDepartmentIds,
    rows,
    skippedRows,
    skipped: summarizeSkippedRows(skippedRows),
    events,
    counts,
    targetExisting: countTargetEntries({ shifts, userHolidays, targetStartKey, targetEndKey }),
  };
}
